import { useStrings } from '../i18n/index.ts';

/**
 * Мелкий бейдж банка на карте: короткое имя в фирменном цвете, по названию эмитента от эквайера
 * («BANCA INTESA»). Банк не из списка — бейдж платёжной системы (Visa, Mastercard, DinaCard, Amex).
 */

type Bank = { match: RegExp; short: string; color: string; ink?: string };

/** Сербские банки и глобальные необанки; ключ — подстрока названия эмитента у эквайера. */
const BANKS: Bank[] = [
  { match: /INTESA/, short: 'Intesa', color: '#1f6f43' },
  { match: /OTP/, short: 'OTP', color: '#52ae30' },
  { match: /RAIFFEISEN/, short: 'RBA', color: '#ffe600', ink: '#111' },
  { match: /UNICREDIT/, short: 'UniCredit', color: '#e2001a' },
  { match: /AIK/, short: 'AIK', color: '#d6001c' },
  { match: /NLB|KOMERCIJALNA/, short: 'NLB', color: '#005ca9' },
  { match: /ERSTE/, short: 'Erste', color: '#2870ed' },
  { match: /HALK/, short: 'Halk', color: '#0093d0' },
  { match: /POSTANSKA|POŠTANSKA|STEDIONICA/, short: 'Pošt.', color: '#f7a600', ink: '#111' },
  { match: /EUROBANK|DIREKTNA/, short: 'Eurobank', color: '#0b3d91' },
  { match: /ALTA/, short: 'ALTA', color: '#c8102e' },
  { match: /ADDIKO/, short: 'Addiko', color: '#e30613' },
  { match: /PROCREDIT/, short: 'ProCredit', color: '#005b9a' },
  { match: /3 ?BANKA|3BANKA/, short: '3B', color: '#e2001a' },
  { match: /API ?BANK/, short: 'API', color: '#0f6ab4' },
  { match: /YETTEL|MOBI ?BANK|TELENOR/, short: 'Yettel', color: '#00a0e0' },
  { match: /REVOLUT/, short: 'Revolut', color: '#191c1f' },
  { match: /WISE|TRANSFERWISE/, short: 'Wise', color: '#9fe870', ink: '#163300' },
  { match: /N26|NUMBER ?26/, short: 'N26', color: '#36a18b' },
  { match: /MONZO/, short: 'Monzo', color: '#ff4d56' },
  { match: /STARLING/, short: 'Starling', color: '#6935d3' },
  { match: /PAYPAL/, short: 'PayPal', color: '#003087' },
  { match: /PAYSERA/, short: 'Paysera', color: '#1b2a4a' },
  { match: /BUNQ/, short: 'bunq', color: '#3eb1a0' },
  { match: /CURVE/, short: 'Curve', color: '#1d1d1d' },
  { match: /TINKOFF|T-?BANK/, short: 'T', color: '#ffdd2d', ink: '#111' },
  { match: /SBER/, short: 'Sber', color: '#21a038' },
  { match: /ALFA/, short: 'Alfa', color: '#ef3124' },
  { match: /HSBC/, short: 'HSBC', color: '#db0011' },
  { match: /BARCLAY/, short: 'Barclays', color: '#00aeef' },
  { match: /CHASE|JPMORGAN/, short: 'Chase', color: '#117aca' },
  { match: /CITI/, short: 'Citi', color: '#003b70' },
  { match: /DEUTSCHE/, short: 'DB', color: '#0018a8' },
  { match: /ING\b/, short: 'ING', color: '#ff6200' },
  { match: /SANTANDER/, short: 'Santander', color: '#ec0000' },
  { match: /BBVA/, short: 'BBVA', color: '#072146' },
  { match: /SOCIETE|SOCIÉTÉ/, short: 'SG', color: '#e60028' },
  { match: /CREDIT AGRICOLE|CRÉDIT AGRICOLE/, short: 'CA', color: '#00843d' },
  { match: /MONOBANK|UNIVERSAL ?BANK/, short: 'mono', color: '#111' },
  { match: /PRIVAT/, short: 'Privat', color: '#75b73b' },
  { match: /KASPI/, short: 'Kaspi', color: '#f14635' },
  { match: /ZIRAAT/, short: 'Ziraat', color: '#e30613' },
  { match: /GARANTI/, short: 'Garanti', color: '#00a94f' },
  { match: /ISBANK|IS BANKASI|İŞ/, short: 'İşbank', color: '#1c4e9d' },
];

const BRANDS: { match: RegExp; short: string; color: string; ink?: string }[] = [
  { match: /VISA/, short: 'VISA', color: '#1a1f71' },
  { match: /MASTER|MC\b/, short: 'MC', color: '#eb001b' },
  { match: /MAESTRO/, short: 'Maestro', color: '#0099df' },
  { match: /DINA/, short: 'Dina', color: '#c8102e' },
  { match: /AMEX|AMERICAN/, short: 'Amex', color: '#2e77bc' },
];

export function bankLook(issuer: string | null, brand: string | null): { short: string; color: string; ink: string } {
  const name = (issuer ?? '').toUpperCase();
  const bank = BANKS.find((b) => b.match.test(name));
  if (bank) return { short: bank.short, color: bank.color, ink: bank.ink ?? '#fff' };
  const system = BRANDS.find((b) => b.match.test((brand ?? '').toUpperCase()));
  if (system) return { short: system.short, color: system.color, ink: system.ink ?? '#fff' };
  return { short: '', color: 'var(--ink-muted)', ink: '#fff' };
}

/** Название карты для списка: эмитент из словаря, иначе платёжная система, иначе «Карта». */
export function cardTitle(card: { issuer: string | null; brand: string | null }, fallback: string): string {
  const name = (card.issuer ?? '').toUpperCase();
  const bank = BANKS.find((b) => b.match.test(name));
  if (bank) return bank.short;
  const system = BRANDS.find((b) => b.match.test((card.brand ?? '').toUpperCase()));
  return system ? system.short : fallback;
}

export function BankBadge(props: { issuer: string | null; brand: string | null; size?: number }) {
  const strings = useStrings();
  const look = bankLook(props.issuer, props.brand);
  const size = props.size ?? 22;
  return (
    <span
      className="bank-badge"
      style={{ background: look.color, color: look.ink, height: size, minWidth: size, fontSize: Math.round(size * 0.42) }}
      aria-label={props.issuer ?? props.brand ?? strings.paymentCardTitle}
    >
      {look.short || '•'}
    </span>
  );
}
